import { getEndCursor } from 'components/Blocks/Pagination/paginationHelpers';
import { DEFAULT_PAGE_SIZE } from 'config/constants';
import { DocumentNode } from 'graphql';
import {
    BrandProductsQueryApi,
    BrandProductsQueryVariablesApi,
    CategoryProductsQueryApi,
    CategoryProductsQueryVariablesApi,
    FlagProductsQueryApi,
    FlagProductsQueryVariablesApi,
    ListedProductConnectionFragmentApi,
} from 'graphql/generated';
import { mapParametersFilter } from 'helpers/filterOptions/MapParametersFilter';
import { calculatePageSize } from 'helpers/loadMore/calculatePageSize';
import { getPageSizeInfo } from 'helpers/loadMore/getPageSizeInfo';
import { getPreviousProductsFromCache } from 'helpers/loadMore/getPreviousProductsFromCache';
import { hasReadAllProductsFromCache } from 'helpers/loadMore/hasReadAllProductsFromCache';
import { mergeProductEdges } from 'helpers/loadMore/mergeProductEdges';
import { readProductsFromCache } from 'helpers/loadMore/readProductsFromCache';
import { useCurrentFilterQuery } from 'helpers/queryParams/useCurrentFilterQuery';
import { useCurrentLoadMoreQuery } from 'helpers/queryParams/useCurrentLoadMoreQuery';
import { useCurrentPageQuery } from 'helpers/queryParams/useCurrentPageQuery';
import { useCurrentSortQuery } from 'helpers/queryParams/useCurrentSortQuery';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { useClient } from 'urql';
import { getSlugFromUrl } from 'utils/getSlugFromUrl';

export const useProductsData = (
    queryDocument: DocumentNode,
    totalProductCount: number,
    shouldRefetch?: boolean,
    additionalParams?: {
        shouldAbortFetchingProducts: boolean;
        abortedFetchCallback: () => void;
        pause?: boolean;
    },
) => {
    const client = useClient();
    const { asPath } = useRouter();
    const currentPage = useCurrentPageQuery();
    const currentFilter = useCurrentFilterQuery();
    const currentSort = useCurrentSortQuery();
    const currentLoadMore = useCurrentLoadMoreQuery();
    const urlSlug = getSlugFromUrl(asPath);
    const mappedFilter = mapParametersFilter(currentFilter);

    const previousLoadMoreRef = useRef(currentLoadMore);
    const previousPageRef = useRef(currentPage);
    const initialPageSizeRef = useRef(calculatePageSize(currentLoadMore));

    const [productsData, setProductsData] = useState(
        readProductsFromCache(
            queryDocument,
            client,
            urlSlug,
            currentSort,
            mappedFilter,
            getEndCursor(currentPage),
            initialPageSizeRef.current,
        ),
    );

    const [areProductsFetching, setAreProductsFetching] = useState(!productsData.products);
    const [isLoadingMoreProducts, setIsLoadingMoreProducts] = useState(false);

    const fetchProducts = async (
        variables: CategoryProductsQueryVariablesApi | FlagProductsQueryVariablesApi | BrandProductsQueryVariablesApi,
        previouslyQueriedProductsFromCache: ListedProductConnectionFragmentApi['edges'] | undefined,
    ) => {
        const productsResponse = await client
            .query<
                CategoryProductsQueryApi | BrandProductsQueryApi | FlagProductsQueryApi,
                typeof variables
            >(queryDocument, variables, shouldRefetch ? { requestPolicy: 'network-only' } : undefined)
            .toPromise();

        const products = getProductsFromQuery(productsResponse.data);

        if (!productsResponse.data || !products) {
            setProductsData({ products: undefined, hasNextPage: false });

            return;
        }

        setProductsData({
            products: mergeProductEdges(previouslyQueriedProductsFromCache, products.edges),
            hasNextPage: products.pageInfo.hasNextPage,
        });
        stopFetching();
    };

    const startFetching = () => {
        if (previousLoadMoreRef.current === currentLoadMore || currentLoadMore === 0) {
            setAreProductsFetching(true);
        } else {
            setIsLoadingMoreProducts(true);
            previousLoadMoreRef.current = currentLoadMore;
        }
    };

    const stopFetching = () => {
        setAreProductsFetching(false);
        setIsLoadingMoreProducts(false);
    };

    useEffect(() => {
        if (additionalParams?.shouldAbortFetchingProducts) {
            additionalParams.abortedFetchCallback();

            return;
        }

        if (additionalParams?.pause) {
            return;
        }

        if (previousPageRef.current !== currentPage) {
            previousPageRef.current = currentPage;
            initialPageSizeRef.current = DEFAULT_PAGE_SIZE;
        }

        const previousProductsFromCache = getPreviousProductsFromCache(
            queryDocument,
            client,
            urlSlug,
            currentSort,
            mappedFilter,
            DEFAULT_PAGE_SIZE,
            initialPageSizeRef.current,
            currentPage,
            currentLoadMore,
            readProductsFromCache,
        );

        if (
            hasReadAllProductsFromCache(
                previousProductsFromCache?.length,
                currentLoadMore,
                currentPage,
                totalProductCount,
            )
        ) {
            return;
        }

        const { pageSize, isMoreThanOnePage } = getPageSizeInfo(!!previousProductsFromCache, currentLoadMore);
        const endCursor = getEndCursor(currentPage, isMoreThanOnePage ? undefined : currentLoadMore);

        startFetching();
        fetchProducts(
            {
                endCursor,
                filter: mappedFilter,
                orderingMode: currentSort,
                urlSlug,
                first: pageSize,
            },
            previousProductsFromCache,
        );
    }, [urlSlug, currentSort, JSON.stringify(currentFilter), currentPage, currentLoadMore]);

    return { ...productsData, areProductsFetching, isLoadingMoreProducts };
};

export const getProductsFromQuery = (
    queryData: CategoryProductsQueryApi | BrandProductsQueryApi | FlagProductsQueryApi | undefined,
) => {
    let products = null;

    if (queryData) {
        if ('category' in queryData) {
            products = queryData.category?.products;
        }
        if ('flag' in queryData) {
            products = queryData.flag?.products;
        }
        if ('brand' in queryData) {
            products = queryData.brand?.products;
        }
    }

    return products;
};
